// src/components/TermsOfUsePublic.jsx

import React, { useEffect } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Bot,
  CalendarDays,
  CreditCard,
  ExternalLink,
  FileText,
  Mail,
  Scale,
  ShieldCheck,
} from "lucide-react";

import "../sass/pages/legal.scss";
import Logo from "../assets/logoClaro.webp";

const LAST_UPDATED = "13 de agosto de 2026";

const TermsOfUsePublic = () => {
  useEffect(() => {
    const previousTitle = document.title;

    document.title =
      "Termos de Uso | PsyAssist Pro";

    let description = document.querySelector(
      'meta[name="description"]'
    );

    const created = !description;

    if (!description) {
      description =
        document.createElement("meta");

      description.setAttribute(
        "name",
        "description"
      );

      document.head.appendChild(
        description
      );
    }

    const previousDescription =
      description.getAttribute("content");

    description.setAttribute(
      "content",
      "Termos de Uso do PsyAssist Pro para utilização da plataforma, assinatura, integrações, automações e PSYA."
    );

    return () => {
      document.title = previousTitle;

      if (created) {
        description.remove();
      } else {
        description.setAttribute(
          "content",
          previousDescription || ""
        );
      }
    };
  }, []);

  return (
    <main className="psy-legal-page">
      <div className="psy-legal-page__ambient psy-legal-page__ambient--blue" />
      <div className="psy-legal-page__ambient psy-legal-page__ambient--violet" />

      <header className="psy-legal-nav">
        <a
          href="/"
          className="psy-legal-nav__brand"
          aria-label="Voltar para PsyAssist Pro"
        >
          <img
            src={Logo}
            alt="PsyAssist Pro"
          />

          <span>
            <strong>PsyAssist Pro</strong>
            <small>
              Termos e condições de uso
            </small>
          </span>
        </a>

        <a
          href="/"
          className="psy-legal-nav__back"
        >
          <ArrowLeft size={16} />
          Voltar ao site
        </a>
      </header>

      <section className="psy-legal-hero">
        <div className="psy-legal-hero__eyebrow">
          <FileText size={15} />
          Regras de utilização da plataforma
        </div>

        <h1>Termos de Uso</h1>

        <p>
          Estes Termos regulam o acesso e a utilização do{" "}
          <strong>PsyAssist Pro</strong>, plataforma de gestão, automação e
          inteligência destinada a psicólogos e clínicas.
        </p>

        <div className="psy-legal-hero__meta">
          <span>Versão 1.0</span>
          <span>Atualizados em {LAST_UPDATED}</span>
          <span>Aplicáveis a psyassist.com.br</span>
        </div>
      </section>

      <div className="psy-legal-shell">
        <aside className="psy-legal-toc">
          <span>Nestes termos</span>
          <a href="#aceite">1. Aceite</a>
          <a href="#servico">2. O serviço</a>
          <a href="#conta">3. Conta</a>
          <a href="#assinatura">4. Assinatura</a>
          <a href="#responsabilidades">5. Responsabilidades</a>
          <a href="#google">6. Google Calendar</a>
          <a href="#psya">7. PSYA e IA</a>
          <a href="#privacidade">8. Privacidade</a>
          <a href="#cancelamento">9. Cancelamento</a>
          <a href="#contato">10. Contato</a>
        </aside>

        <article className="psy-legal-content">
          <section
            id="aceite"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              01
            </span>

            <h2>Aceite dos Termos</h2>

            <p>
              Ao criar uma conta, contratar um plano ou utilizar o PsyAssist
              Pro, o usuário declara ter lido e concordado com estes Termos e
              com a Política de Privacidade vigente.
            </p>

            <p>
              Caso não concorde com alguma condição, o usuário não deve
              concluir a contratação nem utilizar as funcionalidades que
              dependam dessa aceitação.
            </p>
          </section>

          <section
            id="servico"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              02
            </span>

            <h2>O que é o PsyAssist Pro</h2>

            <p>
              O PsyAssist Pro é uma solução tecnológica de apoio à gestão da
              rotina profissional de psicólogos e clínicas. Dependendo do plano
              contratado e da disponibilidade dos recursos, a plataforma pode
              oferecer:
            </p>

            <ul>
              <li>cadastro e organização de clientes;</li>
              <li>agenda, sessões, confirmações e lembretes;</li>
              <li>registros profissionais e documentos;</li>
              <li>
                controles de receitas, despesas, cobranças e relatórios;
              </li>
              <li>comunicações por e-mail e WhatsApp;</li>
              <li>
                integração com Google Calendar e criação de eventos com
                Google Meet;
              </li>
              <li>
                automações e recursos da PSYA baseados em inteligência
                artificial;
              </li>
              <li>
                outros recursos lançados ou aprimorados ao longo da evolução
                do produto.
              </li>
            </ul>

            <div className="psy-legal-callout">
              <BadgeCheck size={18} />

              <p>
                O PsyAssist Pro é uma ferramenta de apoio operacional e de
                organização. A responsabilidade profissional, ética e clínica
                sobre atendimentos, registros, decisões e comunicações permanece
                com o profissional ou organização responsável.
              </p>
            </div>
          </section>

          <section
            id="conta"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              03
            </span>

            <h2>Cadastro, conta e segurança</h2>

            <p>
              O usuário deve fornecer informações verdadeiras e manter seus
              dados de conta atualizados. O acesso é pessoal e as credenciais
              não devem ser compartilhadas com terceiros não autorizados.
            </p>

            <p>
              O usuário é responsável por proteger senha, dispositivos e
              meios de autenticação utilizados para acessar o serviço e deve
              comunicar suspeitas de acesso indevido pelos canais oficiais.
            </p>

            <p>
              Poderemos restringir ou suspender acessos quando houver indícios
              razoáveis de fraude, uso abusivo, violação destes Termos, risco à
              segurança ou determinação legal.
            </p>
          </section>

          <section
            id="assinatura"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              04
            </span>

            <div className="psy-legal-section__heading-with-icon">
              <span>
                <CreditCard size={19} />
              </span>

              <h2>Planos, teste e cobrança</h2>
            </div>

            <p>
              O PsyAssist Pro pode oferecer diferentes planos, preços, períodos
              de teste e campanhas promocionais. As condições válidas no
              momento da contratação são apresentadas antes da conclusão do
              checkout.
            </p>

            <p>
              Quando houver período de teste, a cobrança ocorrerá conforme a
              condição apresentada no fluxo de contratação. Campanhas e cupons
              podem possuir prazo, limite de utilização, planos elegíveis e
              outras condições específicas.
            </p>

            <p>
              O processamento de pagamentos e assinaturas pode ser realizado
              por provedor especializado. Informações completas de cartão não
              precisam ser armazenadas diretamente pelo PsyAssist Pro quando
              são processadas pelo provedor de pagamento.
            </p>

            <p>
              Salvo condição específica informada no momento da contratação, a
              assinatura é recorrente e permanece ativa até cancelamento ou
              ocorrência que impeça sua continuidade.
            </p>
          </section>

          <section
            id="responsabilidades"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              05
            </span>

            <h2>Responsabilidades do usuário</h2>

            <p>
              Ao utilizar a plataforma, o usuário se compromete a:
            </p>

            <ul>
              <li>
                utilizar o serviço de forma lícita, ética e compatível com sua
                atividade profissional;
              </li>
              <li>
                inserir somente dados que possa legitimamente tratar;
              </li>
              <li>
                manter a confidencialidade de informações de clientes e
                pacientes sob sua responsabilidade;
              </li>
              <li>
                revisar comunicações, automações, cobranças, documentos e
                informações configuradas antes de utilizá-las em sua rotina;
              </li>
              <li>
                não tentar acessar contas, dados, sistemas ou funcionalidades
                sem autorização;
              </li>
              <li>
                não utilizar a plataforma para fraude, spam, assédio, violação
                de direitos ou qualquer finalidade ilícita;
              </li>
              <li>
                observar normas profissionais e legislação aplicável à sua
                atuação.
              </li>
            </ul>

            <p>
              O profissional é responsável pela base legal, adequação,
              exatidão e finalidade dos dados de clientes/pacientes que inserir
              na plataforma.
            </p>
          </section>

          <section
            id="google"
            className="psy-legal-section psy-legal-section--google"
          >
            <span className="psy-legal-section__index">
              06
            </span>

            <div className="psy-legal-section__heading-with-icon">
              <span>
                <CalendarDays size={19} />
              </span>

              <h2>
                Integração com Google Calendar e Google Meet
              </h2>
            </div>

            <p>
              A conexão com Google Calendar é opcional. Quando o usuário
              escolher ativá-la, será direcionado ao fluxo oficial de
              autorização do Google e poderá visualizar as permissões
              solicitadas antes de consentir.
            </p>

            <p>
              A integração pode criar, consultar, atualizar e remover eventos
              relacionados aos agendamentos sincronizados e, para sessões
              online, solicitar a criação de dados de conferência para gerar
              link do Google Meet.
            </p>

            <p>
              O usuário pode desconectar a integração nas configurações do
              PsyAssist Pro ou revogar o acesso diretamente em sua Conta
              Google. A desconexão pode interromper sincronizações futuras.
            </p>

            <p>
              O tratamento de dados do Google é detalhado na nossa{" "}
              <a href="/privacidade">
                Política de Privacidade
              </a>.
            </p>
          </section>

          <section
            id="psya"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              07
            </span>

            <div className="psy-legal-section__heading-with-icon">
              <span>
                <Bot size={19} />
              </span>

              <h2>
                PSYA e recursos de inteligência artificial
              </h2>
            </div>

            <p>
              A PSYA e outros recursos de inteligência artificial podem
              organizar informações, gerar resumos, sugestões, insights,
              conteúdos auxiliares e automações a partir do contexto
              disponibilizado pelo usuário.
            </p>

            <p>
              Resultados gerados por inteligência artificial podem conter
              imprecisões, omissões ou interpretações inadequadas. O usuário
              deve revisar o conteúdo antes de utilizá-lo.
            </p>

            <div className="psy-legal-callout">
              <ShieldCheck size={18} />

              <p>
                A PSYA não substitui o julgamento profissional do psicólogo e
                não deve ser utilizada como autoridade autônoma para diagnóstico,
                prescrição, decisão clínica ou condução terapêutica.
              </p>
            </div>
          </section>

          <section
            id="privacidade"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              08
            </span>

            <h2>Privacidade e dados pessoais</h2>

            <p>
              O tratamento de dados pessoais relacionado ao uso da plataforma
              é descrito na{" "}
              <a href="/privacidade">
                Política de Privacidade do PsyAssist Pro
              </a>, que integra estes Termos para fins de transparência sobre
              coleta, uso, armazenamento, compartilhamento e direitos dos
              titulares.
            </p>

            <p>
              O usuário deve observar suas próprias obrigações como profissional
              ou organização responsável pelos dados que inserir no serviço,
              inclusive em relação a dados pessoais sensíveis.
            </p>
          </section>

          <section className="psy-legal-section">
            <span className="psy-legal-section__index">
              09
            </span>

            <h2>Disponibilidade, manutenção e evolução</h2>

            <p>
              Buscamos manter a plataforma disponível e segura, mas serviços
              digitais podem sofrer indisponibilidades temporárias por
              manutenção, atualizações, falhas de provedores, eventos de
              segurança, internet ou outras circunstâncias técnicas.
            </p>

            <p>
              Funcionalidades podem ser aprimoradas, reorganizadas,
              substituídas ou descontinuadas conforme evolução do produto,
              segurança, requisitos legais ou dependências de terceiros.
            </p>
          </section>

          <section className="psy-legal-section">
            <span className="psy-legal-section__index">
              10
            </span>

            <h2>Propriedade intelectual</h2>

            <p>
              A marca, identidade visual, código, interfaces, componentes,
              documentação e demais elementos próprios do PsyAssist Pro são
              protegidos pela legislação aplicável.
            </p>

            <p>
              A assinatura concede ao usuário direito limitado de acesso e uso
              da plataforma enquanto sua conta estiver autorizada, sem
              transferência de titularidade sobre o software ou seus ativos.
            </p>
          </section>

          <section
            id="cancelamento"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              11
            </span>

            <h2>Cancelamento e encerramento</h2>

            <p>
              O usuário pode solicitar cancelamento conforme os recursos
              disponibilizados na plataforma e as condições apresentadas na
              contratação.
            </p>

            <p>
              O encerramento da assinatura não elimina automaticamente toda
              informação existente quando houver necessidade legítima ou
              obrigação legal de conservação. As regras de retenção e direitos
              relacionados a dados pessoais estão descritas na Política de
              Privacidade.
            </p>
          </section>

          <section className="psy-legal-section">
            <span className="psy-legal-section__index">
              12
            </span>

            <div className="psy-legal-section__heading-with-icon">
              <span>
                <Scale size={19} />
              </span>

              <h2>
                Legislação aplicável e alterações
              </h2>
            </div>

            <p>
              Estes Termos são interpretados de acordo com a legislação
              brasileira aplicável, preservados os direitos que não possam ser
              afastados por contrato.
            </p>

            <p>
              Poderemos atualizar estes Termos para refletir mudanças no
              serviço, requisitos legais ou novas funcionalidades. Alterações
              relevantes serão informadas de forma adequada e a versão
              atualizada permanecerá disponível nesta página.
            </p>

            <p>
              Eventuais controvérsias serão submetidas ao foro legalmente
              competente, observadas as regras de proteção aplicáveis ao
              usuário.
            </p>
          </section>

          <section
            id="contato"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              13
            </span>

            <h2>Contato</h2>

            <p>
              Dúvidas relacionadas a estes Termos podem ser encaminhadas para:
            </p>

            <a
              className="psy-legal-contact"
              href="mailto:stardevs09@gmail.com"
            >
              <Mail size={18} />

              <span>
                <small>Suporte PsyAssist Pro</small>
                <strong>stardevs09@gmail.com</strong>
              </span>

              <ExternalLink size={15} />
            </a>
          </section>

          <footer className="psy-legal-document-footer">
            <div>
              <ShieldCheck size={17} />

              <span>
                <strong>PsyAssist Pro</strong>
                <small>
                  Tecnologia para uma rotina profissional mais organizada.
                </small>
              </span>
            </div>

            <div className="psy-legal-document-footer__links">
              <a href="/privacidade">
                Política de Privacidade
              </a>

              <a href="/">
                Página inicial
              </a>
            </div>
          </footer>
        </article>
      </div>
    </main>
  );
};

export default TermsOfUsePublic;
